import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ChatTextIcon,
  ClipboardTextIcon,
  ShapesIcon,
  FilesIcon,
  GridFourIcon,
  UsersIcon,
  GearIcon,
  BellIcon,
  ShieldIcon,
  SidebarSimpleIcon,
  Microphone,
  WarningIcon,
  GitBranchIcon,
  ListChecksIcon,
  FirstAidIcon,
} from "@phosphor-icons/react";
import {
  clearProductTimeout,
  setProductTimeout,
  type ProductTimer,
} from "@/utils/productTimers";
import { useQueryClient } from "@tanstack/react-query";
import fpLogo from "@/assets/fp-logo-v1.png";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  useSidebar,
  useMobileNavDrawer,
  SIDEBAR_MAX_PX,
  SIDEBAR_MIN_PX,
  SIDEBAR_PINNED_PX,
  SIDEBAR_RAIL_PX,
  shellSidebar,
} from "./SidebarContext";
import PaneDivider from "@/components/common/PaneDivider";
import AgentCreditFoot from "./Agents/components/AgentCreditFoot";
import RobotGlyph from "./Agents/RobotGlyph";
import { useIsPhoneViewport } from "../../hooks/useViewportTier";
import useOrgId from "../FpAppV4/hooks/useOrgId";
import WorkspaceHistory from "./WorkspaceHistory";
import {
  preloadWorkspaces,
  type WorkspaceKind,
} from "../FpFundBrain/hooks/useWorkspacesSimple";
import { ENV, IMPERSONATE_USER_ID, BASE_API_URL_V2 } from "../../constants";
import { Tooltip } from "@mui/material";
import SidebarSkeleton from "./SidebarSkeleton";
import StaffOrgSessionNotice from "./StaffOrgSessionNotice";
import SidebarAccount from "./SidebarAccount";
import { requestClaimsShortcuts } from "./Intake/claimsKeys";
import {
  ScrollFade,
  useScrollFade,
} from "./RMS/Search/Components/useScrollFade";
import { useIsEmbedded } from "../Embed/EmbedContext";
import { displayOrganizationName } from "@/utils/organizationName";
import { isEditableTarget } from "@/utils/editableTarget";
import {
  INTAKE_PATH,
  isClaimsOnly,
  isIntakeRoute,
} from "@/utils/productRoutes";
import { usePhiDocumentReview } from "@/deploymentView";

// Show warning when not in production but using production impersonation
const showImpersonationWarning = ENV !== "prod" && Boolean(IMPERSONATE_USER_ID);

// The hover reveal (#12209): the panel opens once the pointer has rested on
// the toggle for a beat, and closes a beat after it leaves, so a pointer
// crossing the rail on its way somewhere never flashes the panel and a
// pointer that slips off the panel's edge does not lose it.
export const HOVER_OPEN_DELAY_MS = 120;
export const HOVER_CLOSE_DELAY_MS = 160;
// The nav is ONE element that widens from the rail to the panel and back
// (Gerry: "that small side panel should expand outwards"): these are the two
// legs of its width transition. The labels and the rest of the panel stay
// laid out at the full width and are revealed by the moving edge; nothing is
// swapped in or out, so no focus is ever lost to an unmount.
export const PANEL_OPEN_MS = 240;
export const PANEL_CLOSE_MS = 200;

const SidebarInner: React.FC = () => {
  const {
    sidebarOpen: pinned,
    setSidebarOpen: setPinned,
    sidebarWidth,
    setSidebarWidth,
    sidebarResizing,
    setSidebarResizing,
    revealHolds,
    setWorkspaceHistoryKey,
    workspaceHistoryKey,
  } = useSidebar();
  // Phone (#11742): the nav renders only inside the overlay drawer there,
  // so it is always the expanded anatomy, and its close affordance closes
  // the drawer instead of writing the stored pin.
  const phone = useIsPhoneViewport();
  const drawer = useMobileNavDrawer();
  const location = useLocation();
  // The usage foot rides in chat and the agents product (#11921, Hunter;
  // #13284): the weekly limit pauses new chats and agent runs and nothing
  // else, so elsewhere it explains nothing the person can act on.
  const inAgents = location.pathname.startsWith("/agents");
  const inChat = location.pathname.startsWith("/chat");
  const navigate = useNavigate();
  const orgId = useOrgId();
  const queryClient = useQueryClient();

  // Get user data from Redux like FpFundBrain does
  const userData = useSelector((state: any) => state.userData);
  // The products the sign-in answer carries, and none before it: a default
  // painted a product for one frame that the organization may not have
  // (#11422 round 17, Chat on a claims-only organization).
  const allowedProducts: string[] = useMemo(
    () =>
      userData?.userEmail
        ? (userData?.organization?.allowed_products ?? [])
        : [],
    [userData?.userEmail, userData?.organization?.allowed_products]
  );
  const phiDocumentReview = usePhiDocumentReview();
  const [gitBranch, setGitBranch] = useState<string>("");

  useEffect(() => {
    if (ENV !== "local") return;
    fetch(`${BASE_API_URL_V2}/api/v2/dev/git-branch`)
      .then((res) => res.json())
      .then((data) => setGitBranch(data.branch || ""))
      .catch(() => {});
  }, []);

  // The hover reveal is the sidebar's own transient state (#12209): never
  // stored, never in context. `pinned` is the stored preference Layout
  // sizes the column from.
  const [hoverOpen, setHoverOpen] = useState(false);
  const openTimer = useRef<ProductTimer | null>(null);
  const closeTimer = useRef<ProductTimer | null>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  // Set while Escape hands focus back to the toggle, so the focus that
  // arrives is not read as "a keyboard reached the rail, open it".
  const refocusingToggleRef = useRef(false);
  // Where a press on the edge handle began, while it is held: the pointer's
  // x and the panel's width at that moment.

  const clearTimers = useCallback(() => {
    clearProductTimeout(openTimer.current);
    clearProductTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  }, []);
  useEffect(() => clearTimers, [clearTimers]);

  const scheduleOpen = useCallback(() => {
    if (pinned || phone) return;
    // Back within the close delay: the pointer overshot the edge and came
    // back, which is exactly what the delay is for. Cancel the close before
    // asking whether there is anything to open.
    clearProductTimeout(closeTimer.current);
    closeTimer.current = null;
    if (hoverOpen || openTimer.current !== null) return;
    openTimer.current = setProductTimeout(() => {
      openTimer.current = null;
      setHoverOpen(true);
    }, HOVER_OPEN_DELAY_MS);
  }, [pinned, phone, hoverOpen]);

  // A popup opened from the panel (the Owner menu, a share or search
  // modal) has its paper outside the host's DOM, so its focus and pointer
  // read as leaving; while one is open the reveal holds. Read through a ref
  // so the schedulers stay stable.
  const held = revealHolds > 0;
  const heldRef = useRef(held);
  heldRef.current = held;
  const hostRef = useRef<HTMLDivElement>(null);

  const scheduleClose = useCallback(() => {
    clearProductTimeout(openTimer.current);
    openTimer.current = null;
    if (heldRef.current) return;
    if (closeTimer.current !== null) return;
    closeTimer.current = setProductTimeout(() => {
      closeTimer.current = null;
      setHoverOpen(false);
    }, HOVER_CLOSE_DELAY_MS);
  }, []);

  // When the last popup closes, the panel is where the pointer and focus
  // say it should be: still open if either is on it, else closing. Only on
  // that release, never on an open: a panel opened by a synthetic hover has
  // no :hover to show for it.
  const wasHeldRef = useRef(held);
  useEffect(() => {
    const wasHeld = wasHeldRef.current;
    wasHeldRef.current = held;
    if (held) {
      // The popup took focus before its hold registered (its focus trap
      // runs first), so a close is already armed: cancel it.
      clearProductTimeout(closeTimer.current);
      closeTimer.current = null;
      return;
    }
    if (!wasHeld || !hoverOpen || pinned) return;
    const host = hostRef.current;
    if (host === null) return;
    if (host.matches(":hover")) return;
    if (host.contains(document.activeElement)) return;
    scheduleClose();
  }, [held, hoverOpen, pinned, scheduleClose]);

  // Pinning (from here, from a shortcut, or a viewer unpinning) ends any
  // hover reveal: the column is the panel now, or the rail is.
  useEffect(() => {
    clearTimers();
    setHoverOpen(false);
  }, [pinned, clearTimers]);

  const togglePin = useCallback(() => {
    setPinned(!pinned);
  }, [pinned, setPinned]);

  // The resize flag is the edge handle's, and the handle can unmount
  // mid-press (⌘B, a viewer unpinning): whatever the press started, this
  // stops, or every width transition would stay off until the next press.
  const shownForResize = pinned || hoverOpen;
  useEffect(() => {
    if (shownForResize) return;
    setSidebarResizing(false);
  }, [shownForResize, setSidebarResizing]);
  useEffect(() => () => setSidebarResizing(false), [setSidebarResizing]);

  // The panel's contents (labels, wordmark, history, notices) stay in the
  // tree while the edge travels back to the rail, so the close reveals them
  // in reverse rather than cutting them; `closing` holds them for the width
  // transition's length. Timed rather than keyed on transitionend, which
  // never fires under reduced motion.
  const shown = pinned || hoverOpen;
  const [closing, setClosing] = useState(false);
  const wasShownRef = useRef(shown);
  // Layout effect: the hold has to be in place before the first frame of
  // the close paints, or the labels blink out and back in for one frame.
  useLayoutEffect(() => {
    const wasShown = wasShownRef.current;
    wasShownRef.current = shown;
    if (shown) {
      setClosing(false);
      return;
    }
    if (!wasShown || phone) return;
    setClosing(true);
    const timer = setProductTimeout(() => setClosing(false), PANEL_CLOSE_MS);
    return () => clearProductTimeout(timer);
  }, [shown, phone]);

  // ⌘B / Ctrl+B toggles the pin on desktop, never from inside a field.
  useEffect(() => {
    if (phone) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented) return;
      if (event.key.toLowerCase() !== "b") return;
      if (!(event.metaKey || event.ctrlKey)) return;
      if (event.altKey || event.shiftKey || event.repeat) return;
      if (isEditableTarget(event.target)) return;
      event.preventDefault();
      togglePin();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [phone, togglePin]);

  // Firefox scroll position fix
  const sidebarRef = useRef<HTMLDivElement>(null);
  // The scroll region's overflow cue: a history that runs past the panel
  // fades out above the foot instead of cutting a row in half at Settings
  // (Hunter: "The way it cuts off at Settings too doesn't look good").
  const { attach: attachNavFade, hasMoreBelow: navHasMoreBelow } =
    useScrollFade();
  const navScrollRef = useCallback(
    (el: HTMLDivElement | null) => {
      sidebarRef.current = el;
      attachNavFade(el);
    },
    [attachNavFade]
  );

  // Check if user has admin permissions for their organization
  const userIsOrgAdmin =
    userData?.permissions?.find(
      (p: any) => p.organizationId === userData?.organization?._id
    )?.admin === true;

  // Managers can reach Settings (where they see an RMS-settings-only view).
  const userIsManager =
    userData?.permissions?.find(
      (p: any) => p.organizationId === userData?.organization?._id
    )?.manager === true;

  useEffect(() => {
    if (pinned && sidebarRef.current) {
      // Firefox-specific fix: reset scroll position when sidebar opens
      // This addresses the bug where Firefox doesn't restore scroll position properly
      const isFirefox = navigator.userAgent.toLowerCase().includes("firefox");
      if (isFirefox) {
        // Use requestAnimationFrame to ensure the DOM has updated
        requestAnimationFrame(() => {
          if (sidebarRef.current) {
            sidebarRef.current.scrollTop = 0;
          }
        });
      }
    }
  }, [pinned]);

  // Detect workspace type from URL
  const workspaceType = useMemo(() => {
    if (location.pathname.startsWith("/matrix")) return "matrix";
    if (location.pathname.startsWith("/chat")) return "chat";
    if (location.pathname.startsWith("/reports")) return "report_instance";
    if (location.pathname.startsWith("/report-templates"))
      return "report_instance";
    if (location.pathname.startsWith("/matrix-templates")) return "matrix";
    if (location.pathname.startsWith("/alerts")) return "alerts";
    if (location.pathname.startsWith("/exposures")) return "exposures";
    if (location.pathname.startsWith("/documents")) return "documents";
    if (location.pathname.startsWith("/crm")) return "crm";
    if (location.pathname.startsWith("/settings")) return "settings";
    return "other"; // default for any other pages
  }, [location.pathname]);

  // Check if current page supports workspaces (team tab)
  // Only show team tab for pages that have meaningful workspace functionality
  const supportsWorkspaces = ["chat", "matrix", "report_instance"].includes(
    workspaceType
  );

  // Start reading workspace history for chat/matrix/reports pages before the
  // user expands the sidebar. Runs regardless of sidebar state and of which
  // subroute they're on; a history list mounted meanwhile shares the read.
  useEffect(() => {
    if (!userData?.userEmail) return;
    const preloadKind: WorkspaceKind | null =
      workspaceType === "chat"
        ? "chat"
        : workspaceType === "matrix"
          ? "matrix"
          : workspaceType === "report_instance"
            ? "report_instance"
            : null;
    if (preloadKind) {
      preloadWorkspaces(queryClient, preloadKind, orgId, userData?.userId);
    }
  }, [
    workspaceType,
    userData?.userEmail,
    userData?.userId,
    orgId,
    queryClient,
  ]);

  // Trigger workspace history key change only when switching workspace types
  useEffect(() => {
    // Only update the key when switching between different workspace types
    // Extract the base path (e.g., "/chat", "/matrix", "/reports")
    const basePath = location.pathname.split("/")[1] || "";

    // Handle reports route - both /reports and /report-templates should use "report_instance"
    let workspaceTypePath;
    if (basePath === "reports" || basePath === "report-templates") {
      // Both the reports landing page, individual reports, and report templates should show report instances
      workspaceTypePath = "report_instance";
    } else {
      workspaceTypePath = basePath;
    }

    // Create a key based on the workspace type, not the full path
    const newKey = workspaceTypePath || "default";
    setWorkspaceHistoryKey(newKey);
  }, [location.pathname, setWorkspaceHistoryKey]);

  // Product menu items from FpFundBrain
  const menuItems = [
    {
      // The mailroom claims review queue (#11422). Gated like every other
      // entry, on a product id the backend emits, which `allowed_products`
      // adds only for the owning organization — so no other org's nav can
      // show it and no frontend org check is needed here. No `phonePath`:
      // the queue is itself the product's collection surface, so the drawer
      // entry targets `path`, like RMS, Documents and the rest. It and the
      // documents product's entry share /documents (round 19, R8), so a
      // deployment shows one of the two: this one where /documents is the
      // queue (`usePhiDocumentReview`), the documents one otherwise.
      path: INTAKE_PATH,
      title: "Documents",
      icon: FirstAidIcon,
      productId: "bcbswy_intake",
    },
    {
      // Phone (#11742 round 3, CD): the drawer's Chat entry targets the
      // Chats hub — the product's collection surface, as Vehicles is
      // RMS's. `path` stays /chat for active-state matching (every chat
      // surface lives under it); `phonePath` overrides only the target.
      path: "/chat",
      phonePath: "/chats",
      title: "Chat",
      icon: ChatTextIcon,
      productId: "chat",
    },
    {
      path: "/rms",
      title: "RMS",
      icon: UsersIcon,
      productId: "crm",
    },
    {
      path: "/matrix",
      title: "Matrix",
      icon: GridFourIcon,
      productId: "querygrid",
    },
    {
      path: "/reports",
      title: "Reports",
      icon: ClipboardTextIcon,
      productId: "report_generation",
    },
    {
      // Subsumes Reports over time (#11523); its own icon so the two are
      // tellable apart while both stay visible during the migration. Gated
      // on the product_artifacts org flag.
      // MERGE HAZARD: the small-screen nav rework rewrote this file on its
      // own branch and a STAGE-3 auto-merge once resolved it WITHOUT the
      // Artifacts / Funds entries. Keep them when reconciling. (There is
      // deliberately NO Automations entry since #11820: automations live
      // inside the Agents pages; do not restore one from an older branch.)
      path: "/artifacts",
      title: "Artifacts",
      icon: ShapesIcon,
      productId: "artifacts",
    },
    {
      path: "/notetaker",
      title: "Notetaker",
      icon: Microphone,
      productId: "notetaker_standalone",
    },
    {
      path: "/alerts",
      title: "Alerts",
      icon: BellIcon,
      productId: "alerts",
    },
    {
      path: "/exposures",
      title: "Exposures",
      icon: ShieldIcon,
      productId: "exposures",
    },
    {
      // Tasks (#11942): the viewer's tasks across agent runs, meetings, chat
      // and by hand. The full Tasks product, which the tasks on an
      // interaction and the chat task tools follow too; the Tasks product
      // alone only shows run tasks on manager and fund pages (Gerry, Oct
      // 14). The agent's Workflow tab, runs and the run's tools are the
      // workflows product, apart from both.
      path: "/tasks",
      title: "Tasks",
      icon: ListChecksIcon,
      color: "bg-emerald-100/80 text-emerald-700",
      productId: "tasks_full",
    },
    {
      path: "/agents",
      title: "Agents",
      icon: RobotGlyph,
      productId: "agents",
      // Gated purely on the org-level product_agents flag — see the
      // filter logic below.
    },
    {
      path: "/documents",
      title: "Documents",
      icon: FilesIcon,
      productId: "manage_documents",
    },
    {
      path: "/settings",
      title: "Settings",
      icon: GearIcon,
      productId: "settings",
      adminOnly: true,
    },
  ];

  const claimsOnly = isClaimsOnly(allowedProducts);
  const filteredMenuItems = menuItems.filter((item) => {
    // A claims-only organization's rail is Claims alone (#11422 round 18):
    // it keeps Settings in the account menu and the admin's own entries,
    // and nothing else it may carry a product id for (Artifacts) is a place
    // its mailroom works.
    if (claimsOnly && item.productId !== "settings" && !item.adminOnly) {
      return item.productId === "bcbswy_intake" && phiDocumentReview;
    }
    // Agents is visible to all users of an org that has the product
    // enabled (#10115). Visibility is purely the org-level flag now — no
    // super-admin gate — so an org's own users see it once it's flipped on.
    if (item.productId === "agents") {
      return allowedProducts.includes("agents");
    }
    // Settings is every member's (#12394, round 26): a member connects their
    // own calendar from Connectors, which is all their Settings holds. A
    // claims-only organization's member has no entry there, so no item;
    // its admins and managers keep theirs.
    if (item.productId === "settings") {
      return (
        userIsOrgAdmin ||
        userIsManager ||
        (Boolean(userData?.organization?._id) && !claimsOnly)
      );
    }
    if (item.adminOnly) {
      return userIsOrgAdmin;
    }
    // One "Documents" entry per deployment (#11422 round 19, R8): the
    // queue's where /documents is the queue, the documents product's
    // otherwise, never both. Each still needs its product.
    if (item.productId === "bcbswy_intake") {
      return phiDocumentReview && allowedProducts.includes("bcbswy_intake");
    }
    if (item.productId === "manage_documents") {
      return !phiDocumentReview && allowedProducts.includes("manage_documents");
    }
    // Notetaker standalone shows when notetaker is enabled
    if (item.productId === "notetaker_standalone") {
      return allowedProducts.includes("notetaker");
    }
    return allowedProducts.includes(item.productId);
  });

  const isProductActive = (path: string): boolean => {
    return (
      location.pathname.startsWith(path) ||
      (path === "/reports" && location.pathname.startsWith("/report-templates"))
    );
  };

  const handleProductNavigation = (path: string) => {
    // Close FileViewer if it's open by dispatching the close event
    window.dispatchEvent(new CustomEvent("closeDocumentPanel"));
    // Navigate to the new product
    navigate(path);
  };

  // Show skeleton while auth data is still loading
  if (!userData?.userEmail) {
    return (
      <SidebarSkeleton
        collapsed={!pinned && !phone}
        onExpand={() => setPinned(true)}
      />
    );
  }

  // The panel's contents are laid out while the nav is wide, or on its way
  // back to the rail; at rest only what fits the rail's 64px shows.
  const expanded = phone || shown || closing;
  const personName: string =
    (userData?.userName as string | null | undefined) ||
    (userData?.userEmail as string | null | undefined) ||
    "";
  const personInitial = personName.charAt(0).toUpperCase();
  const storedOrganizationName: string | null =
    (userData?.organization?.name as string | null | undefined) ?? null;
  const organizationName: string | null = storedOrganizationName
    ? displayOrganizationName(storedOrganizationName)
    : null;
  const toggleLabel = pinned ? "Hide sidebar" : "Show sidebar";
  // What is only there once the nav is wide: hidden (not merely clipped) at
  // rest, so it is out of the tab order and the accessibility tree there.
  const wideOnly = expanded ? "" : "invisible";

  // The one toggle (#12209): it belongs to the host, not to the nav, so it
  // keeps the same spot in every state.
  // No tooltip (Gerry: "get rid of the show sidebar tooltip"): resting on it
  // already answers what it does, by opening the panel.
  const toggle = (
    <button
      ref={toggleRef}
      type="button"
      aria-label={toggleLabel}
      aria-keyshortcuts="Meta+B Control+B"
      onClick={togglePin}
      // The toggle is what reveals the panel (Gerry: "the panel only pops
      // out when you hover over the icon"): resting on it opens, and a
      // keyboard reaching it opens. The product icons below never do; they
      // highlight and navigate.
      onPointerEnter={scheduleOpen}
      onFocus={(event) => {
        if (refocusingToggleRef.current) {
          // Escape handing focus back here is not an arrival.
          refocusingToggleRef.current = false;
          return;
        }
        if (pinned) return;
        if (!event.currentTarget.matches(":focus-visible")) return;
        clearTimers();
        setHoverOpen(true);
      }}
      className="absolute left-[14px] top-3 z-20 grid h-9 w-9 place-items-center rounded-lg text-[#e6e4df]/60 transition-colors hover:bg-white/10 hover:text-[#e6e4df] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
    >
      <SidebarSimpleIcon className="h-5 w-5" />
    </button>
  );

  // The nav: the rail and the panel are ONE element that widens from 64px to
  // 248px and back. Every row's icon sits where the rail shows it; the label
  // beside it, the wordmark, the history and the foot are laid out at the
  // full width and revealed by the moving edge. Over the page (a hover) it
  // is a slide-over with a lift; pinned it is the column beside the page;
  // on phone it fills the drawer. Square in every state.
  const overlay = !phone && !pinned && expanded;
  // The products; Settings is not one of them but an item in the account
  // menu, as in Claude's sidebar (Gerry: "put settings into the profile at
  // the bottom").
  const mainItems = filteredMenuItems.filter(
    (item) => item.productId !== "settings"
  );
  const settingsItem = filteredMenuItems.find(
    (item) => item.productId === "settings"
  );
  const productRow = (item: (typeof filteredMenuItems)[number]) => {
    const isActive = isProductActive(item.path);
    // Phone: an item may target a phone-specific collection
    // surface (#11742 round 3 — Chat lands on the Chats hub).
    const target =
      phone && "phonePath" in item && item.phonePath
        ? item.phonePath
        : item.path;
    const isExactLanding = location.pathname === target;
    return (
      <li key={item.productId}>
        <Link
          to={target}
          aria-label={item.title}
          aria-current={isActive ? "page" : undefined}
          onClick={(e) => {
            if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) {
              return;
            }
            if (isExactLanding) {
              e.preventDefault();
            } else {
              handleProductNavigation(target);
            }
          }}
          // At rest the row is a 36px square around its icon,
          // centred on the rail like the toggle above it (Gerry:
          // "hovering over an icon when the panel is closed should
          // be a square highlight"); wide, it is the whole row.
          // The icon sits at the same x either way. Keyed on
          // `shown` so the tile contracts with the edge rather
          // than after it.
          className={`flex h-9 items-center gap-2.5 rounded-lg text-[13.5px] transition-[background-color,color,width,margin-left,padding-left,padding-right] duration-200 ${
            shown || phone ? "ml-0 w-full px-[12px]" : "ml-[2px] w-9 px-[10px]"
          } ${
            isActive
              ? "bg-white/10 font-medium text-[#e6e4df]"
              : "text-[#e6e4df]/60 hover:bg-white/[0.06] hover:text-[#e6e4df]"
          }`}
        >
          {/* Phone drawer legibility (#11742 round 5 T2, CD):
              labels step 14→16 and icons up to 20 in the drawer —
              max-md is exactly the drawer presentation, since
              below 1024px the sidebar only renders there. The
              desktop icon is 18px (Gerry, #12414: "slightly
              bigger" than the 16 it was) on text-sm's 20px
              leading DELIBERATELY: CD's density reclaim comes
              from line-height and padding, never from type size,
              so text-base's 24px leading is not the scale here. */}
          <item.icon className="h-[18px] w-[18px] flex-shrink-0 max-md:h-5 max-md:w-5" />
          <span className={`whitespace-nowrap max-md:text-[16px] ${wideOnly}`}>
            {item.title}
          </span>
        </Link>
      </li>
    );
  };

  const nav = (
    <nav
      aria-label="Products"
      data-nav-rail=""
      data-nav-panel={
        phone
          ? "drawer"
          : pinned
            ? "pinned"
            : closing
              ? "closing"
              : shown
                ? "hover"
                : undefined
      }
      // Square in every state (Gerry: "It doesnt have to have rounded
      // edges"): the rail, the hover growth and the pinned column are one
      // full-height column at the window's edge.
      className={`flex select-none flex-col overflow-hidden bg-[#0e1013] text-[#e6e4df] ${
        phone ? "h-full w-full" : "absolute inset-y-0 left-0 z-10"
      }`}
      style={
        phone
          ? undefined
          : {
              // Keyed on `shown`, not `expanded`: the edge starts back the
              // moment the reveal ends, while `closing` only keeps the
              // contents laid out for it to pass over. (Keyed on `expanded`
              // it set off, reversed for the hold, then closed late: the
              // stutter Gerry saw.)
              width: shown ? sidebarWidth : SIDEBAR_RAIL_PX,
              // Opening on the design system's collapse curve, closing a
              // little quicker on the drawer's exit curve (Gerry: "smoother
              // and slightly slower", then "should slide just like the
              // notetaker").
              transition: sidebarResizing
                ? "none"
                : shown
                  ? `width ${PANEL_OPEN_MS}ms var(--fp-ease-collapse)`
                  : `width ${PANEL_CLOSE_MS}ms var(--fp-ease-drawer-exit)`,
              // The notetaker rail's lift over its page; the pinned column
              // sits beside the page and needs none.
              boxShadow: overlay
                ? "8px 0 28px rgba(16, 20, 27, 0.22)"
                : undefined,
            }
      }
    >
      {/* Header, outside the scroll region so it stays put while the panel
          scrolls (Gerry: "i want the panel icon and finpilot to stay showing
          even if we scroll down on the panel"): the toggle's spot at the
          left (it is the host's, not the nav's), the wordmark beside it on
          the toggle's centre line. On phone the drawer has no host toggle,
          so the header carries a close in that spot. */}
      <div
        data-nav-header=""
        className={`relative flex h-9 flex-shrink-0 items-center pl-[14px] pr-3 mt-3 ${
          phone ? "w-full" : ""
        }`}
        style={phone ? undefined : { width: sidebarWidth }}
      >
        {phone && (
          <button
            type="button"
            aria-label="Hide navigation"
            onClick={() => drawer?.setOpen(false)}
            className="grid h-9 w-9 place-items-center rounded-lg text-[#e6e4df]/60 hover:bg-white/10 hover:text-[#e6e4df]"
          >
            <SidebarSimpleIcon className="h-5 w-5" />
          </button>
        )}
        {/* Beside the toggle, not centred (Gerry: "make the finpilot
            slightly smaller, closer to the panel icon"): on desktop the
            toggle is the host's, seated at 14px in the header's spot, so the
            wordmark starts a beat after it; on phone the close is in flow. */}
        <button
          type="button"
          onClick={() => handleProductNavigation("/")}
          className={`inline-flex items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 ${
            phone ? "ml-2" : "ml-[44px]"
          } ${wideOnly}`}
          aria-label="Finpilot home"
        >
          {/* The logo mark in the panel's bone, painted through the PNG as a
              mask so it takes the exact text colour rather than a filtered
              approximation. Optically centred on the toggle's icon: the
              mark's ascenders outweigh its one descender, so its box centre
              reads high; the nudge puts the ascender-to-baseline band on the
              icon's centre line. 281x92 source, so 28px tall is 85.5px wide. */}
          <span
            data-nav-wordmark=""
            role="img"
            aria-label="Finpilot"
            className="block h-7 w-[86px] translate-y-[4px] bg-[#e6e4df]"
            style={{
              WebkitMaskImage: `url(${fpLogo})`,
              maskImage: `url(${fpLogo})`,
              WebkitMaskRepeat: "no-repeat",
              maskRepeat: "no-repeat",
              WebkitMaskSize: "contain",
              maskSize: "contain",
            }}
          />
        </button>
      </div>
      <div
        ref={navScrollRef}
        data-nav-scroll=""
        // No painted scrollbar (Gerry: "I dont like that the scrollbar
        // appears like that"): the region scrolls, quietly, as the design
        // system's inner regions do. It takes the height under the header.
        className={`fp-scroll-quiet flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto overflow-x-hidden px-3 pb-3 ${
          phone ? "w-full" : ""
        }`}
        style={phone ? undefined : { width: sidebarWidth }}
      >
        <div className="flex-shrink-0">
          {ENV === "local" && gitBranch && (
            <Tooltip title={gitBranch} placement="right">
              <span
                className={`ml-2 mt-1 flex items-center gap-1 truncate text-xs font-semibold text-[#e6e4df]/55 ${wideOnly}`}
              >
                <GitBranchIcon className="h-4 w-4 flex-shrink-0" />
                {gitBranch}
              </span>
            </Tooltip>
          )}

          {/* Production Impersonation Warning: the badge alone at rest, the
              badge with its words once wide. */}
          {showImpersonationWarning &&
            (expanded ? (
              <div className="mx-2 mt-2 flex items-center gap-2 rounded bg-red-600 px-2 py-1.5">
                <WarningIcon
                  className="h-4 w-4 flex-shrink-0 text-white"
                  weight="fill"
                />
                <span className="text-xs font-medium text-white">
                  Production Data
                </span>
              </div>
            ) : (
              <Tooltip title="Production Data" placement="right">
                <div className="mt-2 flex w-10 justify-center">
                  <div className="rounded bg-red-600 p-1.5">
                    <WarningIcon className="h-4 w-4 text-white" weight="fill" />
                  </div>
                </div>
              </Tooltip>
            ))}
          <StaffOrgSessionNotice collapsed={!expanded} />

          {/* The products. Each row's icon sits at the rail's icon spot, so
              the row reads as an icon at rest and as icon + label once the
              edge has passed the label. The notetaker rail's treatment:
              white for the current product, soft white brightening on hover
              for the rest; the current row keeps a faint tile so the icon
              alone carries "current" at rest. */}
          <ul className="mt-3 flex flex-col gap-0.5">
            {mainItems.map(productRow)}
          </ul>
        </div>

        {/* Workspace History - only show for supported workspace types.
            Phone chat surfaces (#11742 round 3, CD): the conversation list
            lives on the Chats hub — "the drawer moves you between
            products, the hub moves you between conversations within one" —
            so the drawer stands its history down there. Non-chat surfaces
            keep drawer history until they grow hubs of their own. */}
        {supportsWorkspaces && !(phone && workspaceType === "chat") && (
          <div className={`ml-2 ${wideOnly}`} data-sidebar-history="">
            <WorkspaceHistory
              key={workspaceHistoryKey}
              workspaceType={workspaceType}
              supportsWorkspaces={supportsWorkspaces}
              allowedProducts={allowedProducts}
              userIsOrgAdmin={userIsOrgAdmin}
            />
            {/* The history's last child, so it sticks to the scroller's
                bottom without the scroller's gap adding scroll height; the
                inset is the scroller's pb-3. It ends translucent, so the
                next chat still reads through it as "more below" (Gerry: "it
                fades too much"). */}
            {navHasMoreBelow && (
              <ScrollFade ground="rgba(14, 16, 19, 0.7)" inset={12} />
            )}
          </div>
        )}
      </div>

      {/* The column's foot: the agents credit line when in that product
          (the ring alone shows at rest, its label past the edge), then the
          signed-in person, the way the notetaker rail ends (an initial, their
          name, their organization, and a menu with Settings and the way out;
          at rest the initial alone shows, as a rail's avatar does). Outside the
          scroll region, anchored to the column's bottom as the header is to
          its top, so a long history scrolls between them instead of pushing
          it out of view (Hunter: "If you have hundreds of previous chats you
          have to scroll through them all to get to settings/user"). */}
      <div
        data-nav-foot=""
        className={`flex flex-shrink-0 flex-col gap-3 px-3 pb-3 pt-3 ${
          phone ? "w-full" : ""
        }`}
        style={phone ? undefined : { width: sidebarWidth }}
      >
        {(inAgents || inChat) && <AgentCreditFoot collapsed={!expanded} />}
        <SidebarAccount
          initial={personInitial}
          name={personName}
          organizationName={organizationName}
          wideOnly={wideOnly}
          wide={shown || phone}
          settingsPath={settingsItem?.path ?? null}
          onShortcuts={
            isIntakeRoute(location.pathname, phiDocumentReview)
              ? requestClaimsShortcuts
              : undefined
          }
        />
      </div>
    </nav>
  );

  if (phone) return nav;

  return (
    <div
      ref={hostRef}
      data-nav-host=""
      className="relative h-full"
      // The host keeps an open panel open while the pointer is anywhere on
      // it (a pointer that moved from the toggle down into the rows), and
      // closes it a beat after the pointer leaves. Opening is the toggle's
      // alone.
      onPointerEnter={() => {
        if (hoverOpen) scheduleOpen();
      }}
      onPointerLeave={scheduleClose}
      onBlur={(event) => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null))
          return;
        if (heldRef.current) return;
        scheduleClose();
      }}
      onKeyDown={(event) => {
        if (event.key !== "Escape" || !hoverOpen) return;
        event.stopPropagation();
        clearTimers();
        setHoverOpen(false);
        if (document.activeElement !== toggleRef.current) {
          refocusingToggleRef.current = true;
          toggleRef.current?.focus();
        }
      }}
    >
      {toggle}
      {nav}
      {/* The edge handle (Gerry: "Claude allows for expanding the panel if
          you hover over the middle line, it changes the cursor look",
          "should only let you expand by hand when the pane is open"): while
          the panel is open, its right edge takes a column-resize cursor and
          a faint line on hover. Hover-open, a click or a rightward drag
          pins it ("expand by hand"); pinned, a click or a leftward drag
          collapses it. The rail has no handle. It straddles the edge, so
          the boundary line itself (and the gutter beside it) is the hit
          zone, and it lives in the host rather than the nav so the nav's
          overflow clip cannot cut it. A pointer affordance: the toggle is
          the keyboard path. */}
      {shown && (
        // The app's one divider (`PaneDivider`), on the panel's edge. A
        // drag follows the pointer live and persists on release; it stops
        // at the narrowest width and never collapses (Gerry: "when u drag
        // to the very left, it closes automatically, shouldnt do that").
        // A press is the toggle. A pointer affordance only: hidden from
        // assistive tech, whose path is the toggle button.
        <div
          data-nav-edge=""
          className="absolute inset-y-0 z-20 w-0"
          style={{ left: sidebarWidth }}
        >
          <PaneDivider
            label={pinned ? "Collapse sidebar" : "Expand sidebar"}
            placement="left"
            value={sidebarWidth}
            min={SIDEBAR_MIN_PX}
            max={SIDEBAR_MAX_PX}
            pointerOnly
            onDraggingChange={setSidebarResizing}
            onDrag={(dx, start) => setSidebarWidth(start + dx, false)}
            onDragEnd={(dx, start) => {
              setSidebarWidth(start + dx);
              if (!pinned) setPinned(true);
            }}
            onPress={togglePin}
            onReset={() => setSidebarWidth(SIDEBAR_PINNED_PX)}
          />
        </div>
      )}
    </div>
  );
};

/**
 * Embedded in another product's page, the host owns the surrounding chrome, so
 * the app renders no navigation of its own (#11021).
 *
 * Gated here rather than at each of the ~17 `<Sidebar />` call sites: one place
 * to be right, and it covers call sites added later. Returning before
 * `SidebarInner` renders also keeps its hooks and its workspace prefetches from
 * mounting at all, rather than mounting them and hiding the output.
 */
const Sidebar: React.FC = () => {
  const embedded = useIsEmbedded();
  if (embedded) return null;
  return <SidebarInner />;
};

// Marked so Layout places it in the nav column (#14170).
export default shellSidebar(Sidebar);
